import React from 'react';
import { SectionCard, TextField, TextAreaField, ImageField, Repeater } from '../../../components/admin/EditorFields';

/**
 * Helper : String-list Repeater (each item is a raw string)
 * We use the full setData with a path so re-renders keep the correct index.
 */
function StringListRepeater({ label, addLabel, items, path, setData }) {
  return (
    <Repeater
      label={label}
      addLabel={addLabel}
      items={items || []}
      onChange={(next) => setData((d) => {
        const cp = JSON.parse(JSON.stringify(d));
        const keys = path.split('.');
        let cur = cp;
        for (let i = 0; i < keys.length - 1; i++) {
          cur[keys[i]] = cur[keys[i]] || {};
          cur = cur[keys[i]];
        }
        cur[keys[keys.length - 1]] = next;
        return cp;
      })}
      newItem={() => ''}
      renderItem={(item, _, i) => (
        <TextAreaField
          label={`Ligne ${i + 1}`}
          rows={2}
          value={typeof item === 'string' ? item : ''}
          onChange={(v) => setData((d) => {
            const cp = JSON.parse(JSON.stringify(d));
            const keys = path.split('.');
            let cur = cp;
            for (let k = 0; k < keys.length - 1; k++) cur = cur[keys[k]] = cur[keys[k]] || {};
            const arr = cur[keys[keys.length - 1]] || [];
            arr[i] = v;
            cur[keys[keys.length - 1]] = arr;
            return cp;
          })}
        />
      )}
    />
  );
}

export default function AssociationEditor({ data, setData }) {
  const upd = (path, value) => setData((d) => {
    const next = JSON.parse(JSON.stringify(d || {}));
    const keys = path.split('.');
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) {
      cur[keys[i]] = cur[keys[i]] || {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = value;
    return next;
  });

  return (
    <div className="space-y-5">
      {/* HERO */}
      <SectionCard title="Hero (en-tête)">
        <TextField label="Eyebrow" value={data.hero?.eyebrow} onChange={(v) => upd('hero.eyebrow', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Titre" value={data.hero?.title} onChange={(v) => upd('hero.title', v)} />
          <TextField label="Titre en couleur" value={data.hero?.titleHighlight} onChange={(v) => upd('hero.titleHighlight', v)} />
        </div>
        <TextField label="Suffixe (sous les titres)" value={data.hero?.suffix} onChange={(v) => upd('hero.suffix', v)} />
        <TextAreaField label="Description / citation" rows={2} value={data.hero?.description} onChange={(v) => upd('hero.description', v)} />
      </SectionCard>

      {/* CONTEXT */}
      <SectionCard title="Contexte" description="Paragraphes introductifs (politiques publiques…).">
        <StringListRepeater label="Paragraphes" items={data.context?.paragraphs} path="context.paragraphs" setData={setData} />
      </SectionCard>

      {/* ORIGIN */}
      <SectionCard title="Origine du projet" description="L'histoire de Gabriel GAUDEBERT.">
        <TextField label="Titre" value={data.origin?.title} onChange={(v) => upd('origin.title', v)} />
        <StringListRepeater label="Paragraphes" items={data.origin?.paragraphs} path="origin.paragraphs" setData={setData} />
        <StringListRepeater label="Questions posées (liste à puces)" items={data.origin?.questions} path="origin.questions" setData={setData} />
        <TextAreaField label="Conclusion" rows={2} value={data.origin?.conclusion} onChange={(v) => upd('origin.conclusion', v)} />
      </SectionCard>

      {/* ITALY */}
      <SectionCard title="Découverte en Italie" description="Casa dell'Acqua & inspiration.">
        <TextField label="Titre" value={data.italy?.title} onChange={(v) => upd('italy.title', v)} />
        <StringListRepeater label="Paragraphes" items={data.italy?.paragraphs} path="italy.paragraphs" setData={setData} />
        <StringListRepeater label="Statistiques / points clés" items={data.italy?.stats} path="italy.stats" setData={setData} />
        <TextAreaField label="Conclusion" rows={2} value={data.italy?.conclusion} onChange={(v) => upd('italy.conclusion', v)} />
      </SectionCard>

      {/* CONCRETE */}
      <SectionCard title="Transformer l'idée en projet concret">
        <TextField label="Titre" value={data.concrete?.title} onChange={(v) => upd('concrete.title', v)} />
        <StringListRepeater label="Paragraphes" items={data.concrete?.paragraphs} path="concrete.paragraphs" setData={setData} />
        <StringListRepeater label="Soutiens reçus (liste)" items={data.concrete?.support} path="concrete.support" setData={setData} />
        <TextAreaField label="Étape clé (bannière colorée)" rows={2} value={data.concrete?.milestone} onChange={(v) => upd('concrete.milestone', v)} />
        <ImageField label="Photo (inauguration)" value={data.concrete?.photo} onChange={(v) => upd('concrete.photo', v)} />
        <TextField label="Légende photo" value={data.concrete?.photoCaption} onChange={(v) => upd('concrete.photoCaption', v)} />
      </SectionCard>

      {/* NAME */}
      <SectionCard title="Pourquoi « Kiosques à Eau » ?">
        <TextField label="Titre" value={data.name?.title} onChange={(v) => upd('name.title', v)} />
        <StringListRepeater label="Paragraphes" items={data.name?.paragraphs} path="name.paragraphs" setData={setData} />
        <StringListRepeater label="Évocations du mot « fontaine »" items={data.name?.evocations} path="name.evocations" setData={setData} />
        <TextAreaField label="Réalité du concept" rows={2} value={data.name?.reality} onChange={(v) => upd('name.reality', v)} />
        <TextAreaField label="Adoption du nom" rows={2} value={data.name?.adoption} onChange={(v) => upd('name.adoption', v)} />
        <TextField label="Marque déposée (petite mention)" value={data.name?.trademark} onChange={(v) => upd('name.trademark', v)} />
      </SectionCard>

      {/* INDEPENDENT */}
      <SectionCard title="Une association indépendante">
        <TextField label="Titre" value={data.independent?.title} onChange={(v) => upd('independent.title', v)} />
        <StringListRepeater label="Paragraphes" items={data.independent?.paragraphs} path="independent.paragraphs" setData={setData} />
        <TextAreaField label="Objectif" rows={2} value={data.independent?.objective} onChange={(v) => upd('independent.objective', v)} />
        <TextField label="Lien PDF (statuts / agrément)" value={data.independent?.pdfLink} onChange={(v) => upd('independent.pdfLink', v)} />
      </SectionCard>

      {/* OPERATION */}
      <SectionCard title="Fonctionnement de l'association">
        <TextField label="Titre" value={data.operation?.title} onChange={(v) => upd('operation.title', v)} />
        <TextField label="Introduction" value={data.operation?.intro} onChange={(v) => upd('operation.intro', v)} />
        <StringListRepeater label="Missions / éléments" items={data.operation?.items} path="operation.items" setData={setData} />
        <TextAreaField label="Conclusion" rows={2} value={data.operation?.conclusion} onChange={(v) => upd('operation.conclusion', v)} />
      </SectionCard>

      {/* NETWORK */}
      <SectionCard title="Développer un réseau de proximité">
        <TextField label="Titre" value={data.network?.title} onChange={(v) => upd('network.title', v)} />
        <StringListRepeater label="Paragraphes" items={data.network?.paragraphs} path="network.paragraphs" setData={setData} />
        <TextAreaField label="Principe (bannière colorée)" rows={2} value={data.network?.principle} onChange={(v) => upd('network.principle', v)} />
      </SectionCard>

      {/* CIRCLES */}
      <SectionCard title="Les Cercles de Citoyens">
        <TextField label="Titre" value={data.circles?.title} onChange={(v) => upd('circles.title', v)} />
        <TextAreaField label="Description" rows={3} value={data.circles?.description} onChange={(v) => upd('circles.description', v)} />
        <ImageField label="Photo (Cercle)" value={data.circles?.photo} onChange={(v) => upd('circles.photo', v)} />
        <TextField
          label="URL du PDF à télécharger (bouton « En savoir plus »)"
          value={data.circles?.pdfUrl}
          onChange={(v) => upd('circles.pdfUrl', v)}
          hint="Colle l'URL complète du PDF (https://…). Laisse vide pour masquer le bouton."
        />
        <TextField
          label="Libellé du bouton PDF"
          value={data.circles?.pdfLabel}
          onChange={(v) => upd('circles.pdfLabel', v)}
          hint="Par défaut : « En savoir plus »"
        />
      </SectionCard>

      {/* CIRCLE ROLE */}
      <SectionCard title="Le rôle du Cercle de Citoyens">
        <TextField label="Introduction" value={data.circleRole?.intro} onChange={(v) => upd('circleRole.intro', v)} />
        <StringListRepeater label="Missions du cercle" items={data.circleRole?.items} path="circleRole.items" setData={setData} />
      </SectionCard>

      {/* WHY CITIZENS */}
      <SectionCard title="Pourquoi s'appuyer sur les citoyens ?">
        <TextField label="Titre" value={data.whyCitizens?.title} onChange={(v) => upd('whyCitizens.title', v)} />
        <StringListRepeater label="Paragraphes" items={data.whyCitizens?.paragraphs} path="whyCitizens.paragraphs" setData={setData} />
        <TextField label="Intro des bénéfices" value={data.whyCitizens?.benefitsIntro} onChange={(v) => upd('whyCitizens.benefitsIntro', v)} />
        <StringListRepeater label="Bénéfices" items={data.whyCitizens?.benefits} path="whyCitizens.benefits" setData={setData} />
        <TextAreaField label="Conclusion" rows={2} value={data.whyCitizens?.conclusion} onChange={(v) => upd('whyCitizens.conclusion', v)} />
      </SectionCard>

      {/* TRANSITION */}
      <SectionCard title="Une transition portée par les habitants">
        <TextField label="Titre" value={data.transition?.title} onChange={(v) => upd('transition.title', v)} />
        <StringListRepeater label="Paragraphes" items={data.transition?.paragraphs} path="transition.paragraphs" setData={setData} />
        <ImageField label="Photo" value={data.transition?.photo} onChange={(v) => upd('transition.photo', v)} />
        <TextField label="Légende photo" value={data.transition?.photoCaption} onChange={(v) => upd('transition.photoCaption', v)} />
        <TextAreaField label="Message marquant (bloc surligné)" rows={2} value={data.transition?.highlight} onChange={(v) => upd('transition.highlight', v)} />
      </SectionCard>

      {/* FINAL CTA */}
      <SectionCard title="CTA final — Rejoignez l'initiative">
        <TextField label="Titre" value={data.finalCta?.title} onChange={(v) => upd('finalCta.title', v)} />
        <TextAreaField label="Description" rows={3} value={data.finalCta?.description} onChange={(v) => upd('finalCta.description', v)} />
      </SectionCard>
    </div>
  );
}
